import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { NzMessageService } from 'ng-zorro-antd/message';
import { from, Observable, of } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';

import { User } from '../models/user.model';
import { UserDataService } from './user/user-data.service';
import { PermissionService } from './permission.service';
import { phoneToLoginEmail } from '../../shared/common/utils';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  private user: Observable<User | null>;
  get user$(): Observable<User | null> {
    return this.user;
  }

  constructor(
    private auth: AngularFireAuth,
    private db: AngularFirestore,
    private message: NzMessageService,
    private userDataService: UserDataService,
    private permission: PermissionService
  ) {
    this.user = (this.auth.user as any).pipe(
      map((user: any) => {
        if (user == null) {
          return null;
        }
        return { id: user.uid };
      })
    );
  }

  /**
   * Đăng nhập.
   *
   * ⚠️ THỨ TỰ QUAN TRỌNG: xác thực Firebase Auth TRƯỚC, đọc hồ sơ `users` SAU.
   *
   * Bản cũ query `users` theo email ngay đầu hàm — tức là đọc Firestore khi
   * `request.auth` còn null. Nó chỉ chạy được nhờ rule `allow read: if true`
   * trên `users`. Siết rule đó lại là toàn bộ hệ thống không ai đăng nhập
   * được (đã xảy ra ngày 2026-09-29). Đọc sau khi đăng nhập thì chỉ cần
   * `allow read: if signedIn()`, và cũng chỉ đọc đúng document của mình
   * (`users/{uid}`) thay vì quét cả collection.
   *
   * Hệ quả: tài khoản bị khoá nay bị chặn NGAY SAU khi Auth xác thực đúng mật
   * khẩu — đăng xuất lại lập tức, không vào được app.
   */
  async login(email: string, password: string): Promise<Observable<any | null>> {
    // Nhập SĐT (hoặc email của admin) → ghép thành email nội bộ cho Auth.
    email = phoneToLoginEmail(email);

    return from(
      this.auth
        .signInWithEmailAndPassword(email, password)
        .then(async (credential) => {
          const uid = credential.user?.uid || '';
          if (!uid) {
            this.message.error('Đăng nhập thất bại, vui lòng thử lại.');
            return null;
          }

          // Hồ sơ có Document ID = Auth uid (Cloud Function `createUser` đảm
          // bảo điều này). Không có hồ sơ nghĩa là tài khoản chưa chuẩn hoá —
          // Firestore Rules cũng đã fail-closed nên vào cũng không làm gì được.
          const user = await this.userDataService.getUserDocById(uid);
          if (!user || user.deleted === true) {
            await this.auth.signOut();
            this.message.error('Tài khoản không tồn tại hoặc đã bị xoá.');
            return null;
          }

          if (Number(user.login_error_count) >= 3) {
            await this.auth.signOut();
            this.message.error('Tài khoản này đã bị khóa.');
            return null;
          }

          this.message.remove();
          this.userDataService.updateLoginErrorCount(uid, 0);
          // Lưu danh tính người đăng nhập để các màn khác dùng đồng bộ — màn
          // tạo hóa đơn auto-fill đúng người này vào ô Nhân viên.
          this.setCurrentStaff(uid, user.employee_name || user.user_name || '');
          // Đồng bộ role vào localStorage NGAY sau khi Firebase Auth xác thực
          // thành công — để quyền truy cập luôn gắn với phiên Auth thật.
          this.setRole(user.role);
          // Nạp ma trận quyền của vị trí TRƯỚC khi vào trang: guard và menu
          // đọc cache này ngay ở lần điều hướng đầu tiên.
          await this.permission.load(user.role);
          return { id: uid };
        })
        .catch(() => {
          // Sai mật khẩu: KHÔNG tăng được `login_error_count` vì chưa có phiên
          // Auth, mà ghi `users` thì cần quyền. Bản cũ cũng không tăng được
          // (rule ghi luôn đòi đăng nhập) nên đây không phải bước lùi — nhưng
          // đồng nghĩa tính năng khoá sau 3 lần sai HIỆN KHÔNG CHẠY. Muốn chạy
          // thật thì phải đếm ở Cloud Function.
          this.message.error(
            'Số điện thoại hoặc mật khẩu không đúng. Vui lòng kiểm tra lại.'
          );
          return null;
        })
    );
  }

  /** Trạng thái phiên Firebase Auth (null nếu chưa/không còn đăng nhập). */
  get authState$(): Observable<any> {
    return this.auth.authState;
  }

  /**
   * Kiểm tra quyền truy cập dùng chung cho guard.
   * Chỉ cho qua khi CÓ phiên Firebase Auth còn hiệu lực VÀ role trong
   * localStorage nằm trong danh sách cho phép. Nếu phiên Auth đã mất thì
   * dọn luôn role cũ để tránh trạng thái "đăng nhập ma" (role còn nhưng
   * không có auth → mọi thao tác ghi Firestore/Storage đều bị rules chặn).
   */
  canAccess(allowedRoles: string[]): Observable<boolean> {
    return this.auth.authState.pipe(
      take(1),
      map((fbUser: any) => {
        if (fbUser == null) {
          this.clearSession();
          return false;
        }
        const role = localStorage.getItem('role')?.toString();
        return role != null && allowedRoles.indexOf(role) !== -1;
      })
    );
  }

  /** Chỉ cần còn phiên Firebase Auth — dùng cho route không khai báo quyền. */
  isAuthenticated(): Observable<boolean> {
    return this.auth.authState.pipe(
      take(1),
      map((fbUser: any) => {
        if (fbUser == null) {
          this.clearSession();
          return false;
        }
        return true;
      })
    );
  }

  /**
   * Kiểm tra quyền XEM theo feature — bản thay thế `canAccess` cho phân quyền
   * động. Cần phiên Auth còn hiệu lực VÀ vị trí của tài khoản có feature này.
   */
  canAccessFeature(feature: string): Observable<boolean> {
    return this.auth.authState.pipe(
      take(1),
      switchMap((fbUser: any) => {
        if (fbUser == null) {
          this.clearSession();
          return of(false);
        }
        return this.permission.canViewAsync(feature);
      })
    );
  }

  /** Lưu role sau khi đăng nhập Firebase thành công. */
  setRole(role: string | undefined | null): void {
    if (role) {
      localStorage.setItem('role', role.toString());
    }
  }

  /**
   * Ghi lại uid + tên hiển thị của người đang đăng nhập.
   *
   * `staff_uid` chính là Document ID của `users`, và cũng là giá trị đi vào
   * `order.employee_id` khi lên đơn — nhờ vậy doanh số quy được về đúng người
   * đăng nhập, không phụ thuộc vào việc ai chọn gì trong dropdown.
   */
  setCurrentStaff(uid: string, name: string): void {
    localStorage.setItem('staff_uid', uid);
    localStorage.setItem('staff_name', name);
  }

  /** uid của người đang đăng nhập, '' nếu chưa có (vd cache cũ trước bản này). */
  static currentStaffUid(): string {
    return (localStorage.getItem('staff_uid') || '').toString();
  }

  /**
   * Tên hiển thị của người đang đăng nhập, dùng để CHỤP vào dòng đăng ký ca và
   * dòng chấm công — đổi tên tài khoản sau này không làm sai lịch/bảng công cũ.
   */
  static currentStaffName(): string {
    return (
      localStorage.getItem('staff_name') ||
      localStorage.getItem('userNameDisplay') ||
      ''
    ).toString();
  }

  /** Xoá mọi dấu vết phiên đăng nhập phía client. */
  clearSession(): void {
    localStorage.removeItem('role');
    localStorage.removeItem('currentUser');
    localStorage.removeItem('userNameDisplay');
    localStorage.removeItem('staff_uid');
    localStorage.removeItem('staff_name');
    this.permission.clear();
  }

  /** Đăng xuất: kết thúc phiên Firebase Auth rồi dọn localStorage. */
  async logout(): Promise<void> {
    await this.auth.signOut();
    this.clearSession();
  }

  isRoleAdmin() {
    const snap = this.permission.snapshot;
    if (snap) return snap.is_admin || snap.position_id === '1';
    return localStorage.getItem('role')?.toString() === '1';
  }

  /** @deprecated vết cũ của vai trò Đại lý, không còn dùng để routing. */
  isRoleAdminAgency() {
    let role = localStorage.getItem('role')?.toString();
    return role === '1' || role === '3' ? true : false;
  }

  /**
   * Có phiên đăng nhập phía client hay không. KHÔNG so với '1'/'2'/'3' nữa —
   * vị trí bây giờ do Admin tự tạo nên id là chuỗi bất kỳ.
   */
  isLogin() {
    const role = localStorage.getItem('role')?.toString();
    return !!role;
  }
}
